/**
 * Detect if we're running inside the Tauri desktop app
 */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Download YouTube audio locally via yt-dlp (Tauri only).
 * Returns a Uint8Array of the audio file data.
 */
export async function tauriDownloadYouTubeAudio(
  url: string,
  range?: { startSec?: number; endSec?: number }
): Promise<{
  data: Uint8Array;
  duration: number;
  size: number;
}> {
  if (!isTauri()) throw new Error("Not running in Tauri");

  const { invoke } = await import("@tauri-apps/api/core");
  const { readFile, remove } = await import("@tauri-apps/plugin-fs");

  // Call the Rust command to download audio via yt-dlp
  const result = await invoke<{ path: string; duration: number; size: number }>(
    "download_youtube_audio",
    {
      url,
      startSec: range?.startSec ?? null,
      endSec: range?.endSec ?? null,
    }
  );

  // Read the file into memory
  const data = await readFile(result.path);

  // Clean up the temp file
  try {
    await remove(result.path);
  } catch {
    // Not critical if cleanup fails
  }

  return {
    data,
    duration: result.duration,
    size: result.size,
  };
}

/**
 * Get YouTube video info via yt-dlp (Tauri only).
 */
export async function tauriGetYouTubeInfo(url: string): Promise<{
  videoId: string;
  title: string;
  duration: number;
  thumbnail: string;
}> {
  if (!isTauri()) throw new Error("Not running in Tauri");

  const { invoke } = await import("@tauri-apps/api/core");

  return invoke<{
    video_id: string;
    title: string;
    duration: number;
    thumbnail: string;
  }>("get_youtube_formats", { url }).then((r) => ({
    videoId: r.video_id,
    title: r.title,
    duration: r.duration,
    thumbnail: r.thumbnail,
  }));
}
